import React, {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import styled from '@emotion/styled'
import {filterMessages} from 'components/Filters/filterMessages'
import {take} from 'ramda'
import {FacetType} from 'types/Search'
import {Spacing} from 'types/Theme'
import {isFacetSelected} from 'utils/filters'

import {Accordion} from '@vio/atlas/Accordion'
import {Checkbox} from '@vio/atlas/Checkbox'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Link} from '@vio/atlas/Link'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {FacetFilterProps} from '../FacetFilter'

interface FacetFilterListProps
  extends Omit<FacetFilterProps, 'appliedFilters'> {
  title?: string
}

const GridItem = styled(Grid)`
  display: flex;
  min-width: 100%; /* needed for when only a few filters are selected */
`

const GridContainer = styled.div<{
  columns: number
  paddingBottom?: Spacing
}>`
  display: grid;
  grid-template-columns: ${({columns = 1}) => `repeat(${columns}, 1fr)`};
  row-gap: ${cssTheme.layout.spacing.s500};
  padding-bottom: ${({paddingBottom = 's000'}) =>
    cssTheme.layout.spacing[paddingBottom]};
  align-items: center;
`

const Title = styled.div`
  ${cssTheme.typography.labelS};
  margin-bottom: ${cssTheme.layout.spacing.s500};
  text-transform: capitalize;
`

/** Helper used to get the grid spacing from the number of columns */
const calculateGridFromColumns = (columns: number) => {
  switch (columns) {
    case 3:
      return 4

    case 2:
      return 6

    case 1:
      return 12

    default:
      return 4
  }
}

export const FacetFilterList = ({
  title,
  facets,
  searchParamName,
  filters,
  onChange,
  getLabel,
  columns,
  limit,
  contractedMessage,
  showMoreRenderType = 'Accordion'
}: Omit<FacetFilterListProps, 'facetGroups'>) => {
  const {formatMessage} = useIntl()

  const [isExpanded, setIsExpanded] = useState(false)

  const gridSize = calculateGridFromColumns(columns)

  const mainFacets = useMemo(
    () => (limit <= facets.length ? take(limit, facets) : facets),
    [facets, limit]
  )
  const extraFacets = useMemo(
    () => facets.slice(mainFacets.length),
    [facets, mainFacets]
  )
  const renderFacet = (facet: FacetType) => {
    const {id} = facet

    return (
      <GridItem key={`${searchParamName}_${id}_${title}`} mobileSm={gridSize}>
        <Checkbox
          name={`${searchParamName}_${id}`}
          value={id}
          checked={isFacetSelected(filters, id)}
          onChange={() => onChange(searchParamName, id)}
          label={getLabel(id) as React.ReactNode}
        />
      </GridItem>
    )
  }

  const renderAccordionHeader: string = useMemo(() => {
    if (contractedMessage) return contractedMessage
    if (isExpanded) return formatMessage(filterMessages.showLess)
    return formatMessage(filterMessages.showMore)
  }, [contractedMessage, isExpanded, formatMessage])

  const renderExtraFacets = () => {
    if (extraFacets.length === 0) return null
    if (showMoreRenderType === 'Accordion') {
      return (
        <Accordion
          isBottomAligned
          size="sm"
          header={renderAccordionHeader}
          onToggle={setIsExpanded}
        >
          {isExpanded && (
            <GridContainer columns={columns}>
              {extraFacets.map(renderFacet)}
            </GridContainer>
          )}
        </Accordion>
      )
    }
    if (showMoreRenderType === 'Link') {
      return (
        <>
          {isExpanded && (
            <GridContainer columns={columns} paddingBottom="s500">
              {extraFacets.map(renderFacet)}
            </GridContainer>
          )}
          <div onClick={() => setIsExpanded(prev => !prev)} role="presentation">
            <Link size="sm">
              {isExpanded ? (
                <FormattedMessageWrapper defaultMessage="Less" id="less" />
              ) : (
                <FormattedMessageWrapper
                  values={{
                    number: extraFacets.length
                  }}
                  defaultMessage="+{number} more"
                  id="moreWithNumber"
                />
              )}
            </Link>
          </div>
        </>
      )
    }
  }

  return (
    <div>
      {title && <Title>{title}</Title>}
      <GridContainer
        columns={columns}
        paddingBottom={showMoreRenderType === 'Link' ? 's500' : 's600'}
      >
        {mainFacets.map(renderFacet)}
      </GridContainer>
      {renderExtraFacets()}
    </div>
  )
}
